import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Clock, Trophy, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import FlashNumberGame from './FlashNumberGame';
import MathOperationsGame from './MathOperationsGame';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StudentExamView = ({ user }) => {
  const [activeExams, setActiveExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examStarted, setExamStarted] = useState(false);

  useEffect(() => {
    loadActiveExams();
    const interval = setInterval(loadActiveExams, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const loadActiveExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/game-exams/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveExams(response.data);
    } catch (error) {
      console.error('Error loading exams:', error);
    }
  };

  const handleStartExam = (exam) => {
    setSelectedExam(exam);
    setExamStarted(true);
  };

  const handleExamComplete = async (scoreData) => {
    if (!selectedExam) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/game-exams/${selectedExam.id}/submit`,
        scoreData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Exam submitted successfully!');
      setSelectedExam(null);
      setExamStarted(false);
      loadActiveExams();
    } catch (error) {
      console.error('Error submitting exam:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit exam');
      }
      // Reset state to allow viewing exams again
      setSelectedExam(null);
      setExamStarted(false);
    }
  };

  // If exam is started, show the appropriate game with exam config
  if (examStarted && selectedExam) {
    const GameComponent = selectedExam.game_type === 'flash_number' 
      ? FlashNumberGame 
      : MathOperationsGame;
    
    return (
      <div className="space-y-4">
        <Card className="p-4 bg-gradient-to-r from-red-500 to-pink-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg">EXAM MODE: {selectedExam.exam_name}</h3>
              <p className="text-sm opacity-90">Teacher: {selectedExam.teacher_name}</p>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>Ends: {new Date(selectedExam.end_time).toLocaleTimeString()}</span>
            </div>
          </div>
        </Card>
        
        <GameComponent 
          user={user} 
          examMode={true}
          examConfig={selectedExam}
          onExamComplete={handleExamComplete}
        />
      </div>
    );
  }

  // Show available exams
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-3 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg">
          <Trophy className="w-8 h-8 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Active Exams
          </h2>
          <p className="text-gray-600 text-sm">Complete your assigned exams</p>
        </div>
      </div>

      {activeExams.length === 0 ? (
        <Card className="p-12 text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Active Exams</h3>
          <p className="text-gray-600">You don't have any active exams at the moment</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activeExams.map(exam => {
            const endTime = new Date(exam.end_time);
            const now = new Date();
            const timeLeft = Math.max(0, Math.floor((endTime - now) / 60000)); // minutes left
            
            return (
              <Card key={exam.id} className="p-6 border-2 border-orange-200 hover:shadow-xl transition-all">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-bold text-xl mb-1">{exam.exam_name}</h3>
                    <p className="text-sm text-gray-600">Teacher: {exam.teacher_name}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Trophy className="w-4 h-4 text-purple-600" />
                      <span className="font-medium">
                        {exam.game_type === 'flash_number' ? 'Flash Number Game' : 'Math Operations'}
                      </span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                        {exam.difficulty}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-orange-600" />
                      <span className={timeLeft < 10 ? 'text-red-600 font-bold' : 'text-gray-700'}>
                        {timeLeft} minutes left
                      </span>
                    </div>
                  </div>

                  {exam.game_config && (
                    <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
                      <div className="font-semibold text-gray-700">Exam Details:</div>
                      {exam.game_type === 'flash_number' && (
                        <>
                          <div>• {exam.game_config.number_of_flashes} numbers will flash</div>
                          <div>• {exam.game_config.digit_count} digit(s) each</div>
                          <div>• {exam.game_config.flash_interval}s flash interval</div>
                          <div>• Calculate their sum</div>
                        </>
                      )}
                      {exam.game_type === 'math_operations' && (
                        <>
                          <div>• {exam.game_config.number_of_questions} questions</div>
                          <div>• {exam.game_config.operation} problems</div>
                          <div>• {exam.difficulty} difficulty</div>
                        </>
                      )}
                    </div>
                  )}

                  <Button 
                    onClick={() => handleStartExam(exam)}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                    size="lg"
                  >
                    Start Exam
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentExamView;
